// Fills the DG Summary sheet's photo-evidence slots with survey photos.
//
// The template marks each slot with a caption pair ("PHOTO 01" over
// "Unit 1 — full unit" / "General Condition") in columns B, F, and J; the
// picture box spans the 13 rows above the caption, three slots across —
// matching the anchors in the hand-filled reference workbook. Photos come
// from the survey tree: a slot describing "Unit N" pulls that unit's
// evidence/photos, anything else pulls from the fields under its page's
// section (nearest preceding SECTION header), and every photo is used at
// most once. Slots with no matching photo are left empty.

import type { AttributeField } from "./types.ts";
import type { SheetImage } from "./xlsxTemplatePatcher.ts";
import { findSheetName, insertSheetImages, readSheetCells, readSheetGeometry } from "./xlsxTemplatePatcher.ts";
import { isNotApplicable } from "./gcEvaluationFill.ts";
import { assetUnit, normSerial, SUMMARY_SHEET_RE } from "./exportCore.ts";

const SUMMARY_SHEET = "DG Leasing - Summary";
const SLOT_COLUMNS: Record<string, number> = { B: 1, F: 5, J: 9 }; // zero-based anchor cols
const SLOT_WIDTH = 3;
const SLOT_HEIGHT = 13;
const MAX_PHOTOS = 30;
const FETCH_CONCURRENCY = 4;

interface PhotoSlot {
  captionRow: number; // 1-based
  col0: number;
  description: string;
  section: string;
}

/** Pure core: locate photo slots and the section each belongs to. */
export function findPhotoSlots(cells: Map<string, string>): PhotoSlot[] {
  // Section titles: "SECTION NN" in column B with the human title one row below.
  const sectionRows: Array<{ row: number; title: string }> = [];
  for (const [ref, text] of cells) {
    const m = ref.match(/^B(\d+)$/);
    if (m && /^SECTION \d+/i.test(text.trim())) {
      const title = cells.get(`B${Number(m[1]) + 1}`) ?? "";
      sectionRows.push({ row: Number(m[1]), title: title.trim() });
    }
  }
  sectionRows.sort((a, b) => a.row - b.row);

  const slots: PhotoSlot[] = [];
  for (const [ref, text] of cells) {
    const m = ref.match(/^([BFJ])(\d+)$/);
    if (!m || !/^PHOTO \d+/i.test(text.trim())) continue;
    const row = Number(m[2]);
    const description = (cells.get(`${m[1]}${row + 1}`) ?? "").trim();
    const section = [...sectionRows].reverse().find((s) => s.row < row)?.title ?? "";
    slots.push({ captionRow: row, col0: SLOT_COLUMNS[m[1]], description, section });
  }
  return slots.sort((a, b) => a.captionRow - b.captionRow || a.col0 - b.col0);
}

/**
 * All photo/evidence URLs under fields whose title matches the predicate.
 * `leafFilter` additionally gates by the owning leaf's own title (used to
 * deprioritize nameplate close-ups for "full unit" slots).
 */
function collectPhotoUrls(
  fields: AttributeField[],
  match: (title: string) => boolean,
  leafFilter?: (title: string) => boolean,
  matchedAncestor = false
): string[] {
  const urls: string[] = [];
  for (const field of fields) {
    const matches = matchedAncestor || match(field.title ?? "");
    if (field.type === "node") {
      urls.push(...collectPhotoUrls(field.fields, match, leafFilter, matches));
      continue;
    }
    if (!matches) continue;
    if (leafFilter && !leafFilter(field.title ?? "")) continue;
    const value = (field as { value?: unknown }).value;
    if (field.type === "photos" && Array.isArray(value)) {
      urls.push(...value.filter((u): u is string => typeof u === "string"));
    }
    const evidence = (field as { evidence?: unknown }).evidence;
    if (Array.isArray(evidence)) {
      urls.push(...evidence.filter((u): u is string => typeof u === "string"));
    }
  }
  return urls;
}

/** Questions whose photos are nameplate/serial close-ups, not full-unit shots. */
const NAMEPLATE_RE = /model|serial|name\s*plate|nameplate/i;

/** True when the survey actually has answers for unit N — absent units must
 * leave their slot empty instead of pulling photos from asset-row fallbacks. */
function unitHasAnswers(fields: AttributeField[], unit: string): boolean {
  const re = new RegExp(`(?:unit|hvac|rtu)\\s*${unit}\\b`, "i");
  const walk = (list: AttributeField[]): boolean =>
    list.some((field) => {
      if (field.type === "node") return walk(field.fields);
      if (!re.test(field.title ?? "")) return false;
      const value = (field as { value?: unknown }).value;
      if (Array.isArray(value)) return value.length > 0;
      if (isNotApplicable(value)) return false; // "N/A" reads as unanswered
      return value !== null && value !== undefined && String(value).trim() !== "";
    });
  return walk(fields);
}

/** Property asset with its full-shot / nameplate photo pair. */
export interface SlotAsset {
  name: string;
  category: string;
  assetPhotoUrl: string | null;
  nameplatePhotoUrl: string | null;
  /** Joins the asset to its surveyed unit (same nameplate). */
  serialNumber?: string | null;
}

/** Unit number only from a number adjacent to a unit-ish word — "5 Ton
 * Rheem RTU" must not read as unit 5. */
function assetUnitNumber(name: string): number | null {
  const n = Number(name.match(/(?:hvac|rtu|unit)[\s#._-]*(\d+)/i)?.[1]);
  return Number.isFinite(n) && n >= 1 && n <= 12 ? n : null;
}

/** Assets belonging to a slot's section, ordered by their unit number. */
function sectionAssets(assets: SlotAsset[], section: string): SlotAsset[] {
  const key = section.toLowerCase();
  return assets
    .filter(
      (a) => a.category.toLowerCase().includes(key) || a.name.toLowerCase().includes(key)
    )
    .sort((a, b) => {
      const na = assetUnitNumber(a.name) ?? Infinity;
      const nb = assetUnitNumber(b.name) ?? Infinity;
      return na - nb || a.name.localeCompare(b.name);
    });
}

/** Answer lookup (SurveyAnswers) — structural type to stay import-light. */
export interface UnitAnswerLookup {
  num(title: string): number | null;
  text(title: string): string | null;
}

/** gs:// → authenticated qa-video proxy URL; null when no token is available. */
export type GcsProxy = (gsUri: string) => string | null;

/** Prod_data evidence photos indexed by section, by HVAC unit, and by question. */
export interface ProdEvidenceIndex {
  section(sectionLower: string, nameplate: boolean): string[];
  unit(unitNumber: number, nameplate: boolean): string[];
  /** Photos from questions whose title matches the predicate (designated source). */
  matching(predicate: (title: string) => boolean): string[];
}

const EMPTY_PROD_EVIDENCE: ProdEvidenceIndex = {
  section: () => [],
  unit: () => [],
  matching: () => [],
};

const unitFromText = (t: string): number | null => {
  const n = Number(t.match(/(?:hvac|rtu|unit)[\s#._-]*(\d+)/i)?.[1]);
  return Number.isFinite(n) && n >= 1 && n <= 12 ? n : null;
};

/**
 * Photo URL from a prod_data evidence item. Surveyor photos are stored as
 * `{ url }` (https, direct); reviewer-pasted screenshots as `{ gcs_uri }`
 * (gs://, private bucket) — resolvable only through the authenticated
 * qa-video proxy, so gs:// items are dropped when no proxy/token is given.
 */
function evidenceItemUrl(item: unknown, gsProxy?: GcsProxy): string | null {
  if (typeof item === "string") {
    return item.startsWith("gs://") ? gsProxy?.(item) ?? null : item;
  }
  if (item && typeof item === "object") {
    const o = item as { url?: string; gcs_uri?: string };
    if (o.url) return o.url;
    if (o.gcs_uri) return gsProxy?.(o.gcs_uri) ?? null;
  }
  return null;
}

/**
 * Indexes prod_data's evidence photos by section and by HVAC unit using
 * prod_data's own self-describing `section`/`question_title` — no tree-id
 * matching. This is where reviewer-pasted (gcs_uri) evidence enters the
 * export; the resolved field tree only carries the direct `.url` shape.
 */
export function collectProdDataEvidence(
  prodData: Record<string, unknown> | null | undefined,
  gsProxy?: GcsProxy
): ProdEvidenceIndex {
  if (!prodData || typeof prodData !== "object") return EMPTY_PROD_EVIDENCE;
  interface Photo {
    url: string;
    sectionLower: string;
    unit: number | null;
    nameplate: boolean;
    title: string;
  }
  const photos: Photo[] = [];

  for (const raw of Object.values(prodData)) {
    const e = raw as {
      question_title?: string;
      section?: string;
      section_path?: Array<{ title?: string }>;
      evidence?: unknown;
    };
    if (!e || typeof e !== "object" || !Array.isArray(e.evidence)) continue;
    const title = String(e.question_title ?? "");
    const sectionLower = String(
      e.section ?? e.section_path?.[e.section_path.length - 1]?.title ?? ""
    ).toLowerCase();
    const unit = unitFromText(title) ?? unitFromText(sectionLower);
    const nameplate = NAMEPLATE_RE.test(title);
    for (const item of e.evidence) {
      const url = evidenceItemUrl(item, gsProxy);
      if (url) photos.push({ url, sectionLower, unit, nameplate, title });
    }
  }

  return {
    section: (s, np) =>
      photos.filter((p) => p.sectionLower === s && p.nameplate === np).map((p) => p.url),
    unit: (u, np) => photos.filter((p) => p.unit === u && p.nameplate === np).map((p) => p.url),
    matching: (pred) => photos.filter((p) => pred(p.title)).map((p) => p.url),
  };
}

/**
 * Designated first-choice photo source per slot group: the question whose
 * reviewer evidence best represents the section/unit. Parking's condition
 * question holds the lot-condition photos; each HVAC unit's Action question
 * holds that unit's rooftop shot. Everything else is fallback.
 */
const PREFERRED_SECTION_QUESTION: Array<{ section: RegExp; question: RegExp }> = [
  { section: /parking/i, question: /which photo best matches.*parking lot condition/i },
];
const unitActionQuestion = (unit: number) =>
  new RegExp(`(?:hvac|rtu|unit)\\s*${unit}\\b.*\\baction\\b`, "i");

/** Pure core: assign a photo URL to each slot, never reusing a photo. */
export function assignSlotPhotos(
  slots: PhotoSlot[],
  fields: AttributeField[],
  assets: SlotAsset[] = [],
  answers?: UnitAnswerLookup,
  prodEvidence: ProdEvidenceIndex = EMPTY_PROD_EVIDENCE
): Array<{ slot: PhotoSlot; urls: string[] }> {
  // Unit existence must use the same robust signal as the rest of the export:
  // the (label-augmented) answers — raw tree titles drift from the "Unit N"
  // wording and would blank every unit slot.
  const unitExists = (n: string): boolean => {
    if (answers) {
      const tons = answers.num(`Unit ${n} — Tonnage`);
      if (tons !== null && tons > 0) return true;
      if (answers.text(`Unit ${n} — Action`)) return true;
      if (answers.text(`Unit ${n} — Serial Number`)) return true;
    }
    return unitHasAnswers(fields, n);
  };

  // Serial joins an asset row to its surveyed unit — the survey and the asset
  // were read off the same nameplate, and these surveys' asset rows carry no
  // unit number in their names.
  const serialToUnit = new Map<string, number>();
  if (answers) {
    for (let n = 1; n <= 12; n++) {
      const s = normSerial(answers.text(`Unit ${n} — Serial Number`));
      if (s && !serialToUnit.has(s)) serialToUnit.set(s, n);
    }
  }
  // A slot reserves its primary photo so no other slot reuses it, but keeps the
  // rest of its (deduped, still-available) pool as ordered fallbacks: if the
  // primary fails to download — e.g. a reviewer gs:// shot when the QA proxy is
  // down — the fetch stage drops to the next candidate instead of leaving the
  // slot empty. Alternates aren't globally reserved, so two slots may share one;
  // a duplicated photo only surfaces if both primaries fail, which beats a gap.
  const used = new Set<string>();
  const take = (pool: Array<string | null>): string[] => {
    const seen = new Set<string>();
    const candidates: string[] = [];
    for (const u of pool) {
      if (!u || seen.has(u) || used.has(u)) continue;
      seen.add(u);
      candidates.push(u);
    }
    if (candidates.length > 0) used.add(candidates[0]);
    return candidates;
  };

  const assignments: Array<{ slot: PhotoSlot; urls: string[] }> = [];
  for (const slot of slots) {
    const sectionMatch = slot.section
      ? (t: string) => t.toLowerCase().includes(slot.section.toLowerCase())
      : null;
    const unit = slot.description.match(/unit\s*(\d+)/i);
    // A unit the survey doesn't have gets no photo — asset rows beyond the
    // surveyed unit count must not leak into its slot.
    if (unit && !unitExists(unit[1])) continue;
    const notPlate = (t: string) => !NAMEPLATE_RE.test(t);
    // Surveys title unit questions either "Unit 4 — …" or "HVAC 4 - …" —
    // a photo hangs off whichever dialect this survey uses.
    const unitMatch = unit
      ? (t: string) => new RegExp(`(?:unit|hvac|rtu)\\s*${unit[1]}\\b`, "i").test(t)
      : null;

    // Assets carry an explicit full-shot/nameplate pair, so "Unit N" slots
    // take the matching asset's full-equipment photo first — by the number in
    // the asset's name, else by position. A unit's designated asset photo is
    // exempt from the no-reuse rule: if two units share one wide shot, showing
    // it twice beats degrading to a nameplate. Survey-question photos follow,
    // and nameplate close-ups only beat an empty slot.
    // Only an asset identified as THIS unit (by serial, else by its name) may
    // supply its photo — position is not identity (a backroom heater sitting
    // 4th in the list is not Unit 4).
    const slotAssets = sectionAssets(assets, slot.section);
    const unitAsset = unit
      ? (slotAssets.find(
          (a) => assetUnit(a, serialToUnit, assetUnitNumber) === Number(unit[1])
        ) ?? null)
      : null;
    // Designated source question first (parking-condition / HVAC-N-Action),
    // then the rest of the section/unit evidence, then the asset shot, then
    // nameplate close-ups last — a slot prefers a real photo of the item over
    // an asset thumbnail, and never surfaces a plate when a wider shot exists.
    // Unit slots draw ONLY from their own unit; a 4-unit site leaves slots 5-6
    // empty rather than borrowing section-wide shots.
    const sectionKey = slot.section.toLowerCase();
    const pool: Array<string | null> = unit
      ? [
          ...prodEvidence.matching((t) => unitActionQuestion(Number(unit[1])).test(t)),
          ...prodEvidence.unit(Number(unit[1]), false),
          ...(unitMatch ? collectPhotoUrls(fields, unitMatch, notPlate) : []),
          unitAsset?.assetPhotoUrl ?? null,
          ...prodEvidence.unit(Number(unit[1]), true),
          unitAsset?.nameplatePhotoUrl ?? null,
          ...(unitMatch ? collectPhotoUrls(fields, unitMatch) : []),
        ]
      : [
          ...prodEvidence.matching((t) =>
            PREFERRED_SECTION_QUESTION.some(
              (p) => p.section.test(sectionKey) && p.question.test(t)
            )
          ),
          ...prodEvidence.section(sectionKey, false),
          ...(sectionMatch ? collectPhotoUrls(fields, sectionMatch, notPlate) : []),
          ...slotAssets.map((a) => a.assetPhotoUrl),
          ...prodEvidence.section(sectionKey, true),
          ...(sectionMatch ? collectPhotoUrls(fields, sectionMatch) : []),
        ];
    const urls = take(pool);
    if (urls.length > 0) assignments.push({ slot, urls });
  }
  return assignments;
}

const RESIZE_FUNCTIONS_REGION = "us-central1";
/** Deployed name of the `images` group's export — the group prefixes it. */
const RESIZE_FUNCTION_NAME = "images-resizeImage";
const RESIZE_MAX_LONG_SIDE = 500;
const RESIZE_JPEG_QUALITY = 75;

/**
 * Origin of the existing, already-proven `images-resizeImage` Cloud Function
 * (Node + sharp) — same one the mobile app's ResizedImage and the web form's
 * SortableFile thumbnails already call. Delegating to it here (rather than
 * decoding/resizing images in-process) sidesteps a run of Deno-specific
 * failures hit building a local pipeline: no working OffscreenCanvas 2d
 * context, no reliable `Buffer` global, and a pngjs ChunkStream bug — plus it
 * moves the actual CPU/memory-heavy work off the edge function's tight budget
 * entirely (real "CPU Time exceeded" / "Memory limit exceeded" errors were
 * showing up before this).
 */
const RESIZE_FUNCTIONS_PROJECT_ID = () =>
  (globalThis as { Deno?: { env: { get(key: string): string | undefined } } }).Deno?.env.get(
    "FIREBASE_PROJECT_ID"
  ) ?? "disco-outpost-268223";

/**
 * Wraps a source photo URL in a call to `images-resizeImage`'s aspect-
 * preserving mode (`maxLongSide`): no local crop (the existing `coverSrcRect`
 * logic already crops visually via Excel's `<a:srcRect>`, independent of the
 * embedded image's own aspect), always JPEG out, with sharp's EXIF rotation
 * and its own byte-budget quality fallback already built in.
 */
export function buildResizeImageUrl(sourceUrl: string): string {
  const origin = `https://${RESIZE_FUNCTIONS_REGION}-${RESIZE_FUNCTIONS_PROJECT_ID()}.cloudfunctions.net`;
  const params = new URLSearchParams({
    imageUrl: sourceUrl,
    maxLongSide: String(RESIZE_MAX_LONG_SIDE),
    quality: String(RESIZE_JPEG_QUALITY),
  });
  return `${origin}/${RESIZE_FUNCTION_NAME}?${params.toString()}`;
}

/**
 * Pixel dimensions straight from the encoded bytes (JPEG SOF / PNG IHDR) — no
 * canvas, so it works even when the OffscreenCanvas crop path is unavailable.
 * Returns null for anything it can't read (WebP, truncated, etc.).
 */
export function imageSize(data: ArrayBuffer): { w: number; h: number } | null {
  const b = new Uint8Array(data);
  // PNG: IHDR width/height are the two big-endian uint32s at offset 16/20.
  if (b.length > 24 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) {
    const dv = new DataView(data);
    return { w: dv.getUint32(16), h: dv.getUint32(20) };
  }
  // JPEG: walk the marker segments to the Start-Of-Frame, read its dimensions.
  if (b.length > 4 && b[0] === 0xff && b[1] === 0xd8) {
    let i = 2;
    while (i + 8 < b.length) {
      if (b[i] !== 0xff) {
        i++;
        continue;
      }
      const marker = b[i + 1];
      const isSOF =
        marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
      if (isSOF) return { w: (b[i + 7] << 8) | b[i + 8], h: (b[i + 5] << 8) | b[i + 6] };
      const len = (b[i + 2] << 8) | b[i + 3];
      if (len <= 0) break;
      i += 2 + len;
    }
  }
  return null;
}

/**
 * Center-crop insets (1/1000 %) that fit an image of `iw`×`ih` into a frame of
 * `targetAspect` (width/height) — the excess of the longer side is trimmed
 * evenly. Feeds <a:srcRect> so Excel crops rather than distorts.
 */
export function coverSrcRect(
  iw: number,
  ih: number,
  targetAspect: number
): { l: number; t: number; r: number; b: number } {
  const PCT = 100000;
  const imgAspect = iw / ih;
  if (imgAspect > targetAspect) {
    const inset = Math.round(((1 - targetAspect / imgAspect) / 2) * PCT);
    return { l: inset, t: 0, r: inset, b: 0 };
  }
  const inset = Math.round(((1 - imgAspect / targetAspect) / 2) * PCT);
  return { l: 0, t: inset, r: 0, b: inset };
}

/** Short label for logs — hide the token, keep the source identifiable. */
const photoLabel = (url: string) =>
  url.includes("/v1/qa-video") ? `qa-video(${url.split("uri=")[1]?.split("&")[0]?.slice(-24) ?? "?"})` : url.slice(0, 80);

/**
 * The resized output should always be small (long side capped at 500px) —
 * this is just a backstop against something going wrong upstream, not a
 * "decode budget" (there's no local decode anymore).
 */
const MAX_RESIZED_IMAGE_BYTES = 10 * 1024 * 1024;

export async function fetchImage(url: string): Promise<{ data: ArrayBuffer; ext: "jpeg" | "png" } | null> {
  try {
    const res = await fetch(buildResizeImageUrl(url));
    if (!res.ok) {
      console.warn(`[summary photos] resize fetch ${res.status} ${res.statusText} — ${photoLabel(url)}`);
      return null;
    }
    const declaredLength = Number(res.headers.get("content-length"));
    const bytes = await res.arrayBuffer();
    // A cut-short response (a real prod incident hit this on the qa-video
    // proxy, one layer further upstream) is indistinguishable from a small
    // successful one unless checked against what it promised.
    if (Number.isFinite(declaredLength) && declaredLength > 0 && bytes.byteLength < declaredLength) {
      console.warn(
        `[summary photos] truncated resize response (${bytes.byteLength}/${declaredLength} bytes) — ${photoLabel(url)}`
      );
      return null;
    }
    if (bytes.byteLength > MAX_RESIZED_IMAGE_BYTES) {
      console.warn(
        `[summary photos] resize response unexpectedly large (${Math.round(bytes.byteLength / 1024 / 1024)}MB) — ${photoLabel(url)}`
      );
      return null;
    }
    // images-resizeImage's maxLongSide mode always returns JPEG.
    return { data: bytes, ext: "jpeg" };
  } catch (err) {
    console.warn(
      `[summary photos] resize fetch failed (${err instanceof Error ? err.message : err}) — ${photoLabel(url)}`
    );
    return null;
  }
}

/**
 * Fetches the assigned photos and inserts them into the Summary sheet's photo
 * slots. Returns the workbook unchanged when the sheet has no slots or no
 * photos resolve. Operates on the already-stamped workbook bytes.
 */
export async function fillSummaryPhotos(
  workbook: Blob,
  fields: AttributeField[],
  assets: SlotAsset[] = [],
  answers?: UnitAnswerLookup,
  onProgress?: (message: string) => void,
  prodData?: Record<string, unknown> | null,
  gsProxy?: GcsProxy
): Promise<{ data: Blob; photos: number }> {
  const buf = await workbook.arrayBuffer();
  // The tab name drifts between template revisions ("… - Summary",
  // "… — Summary", "DG Leasing ONLY - Summary") — resolve it by anchor.
  const summarySheet = (await findSheetName(buf, SUMMARY_SHEET_RE)) ?? SUMMARY_SHEET;
  const cells = await readSheetCells(buf, summarySheet);
  // warn (not info) so the diagnostics survive the console's default level
  // filter — this is the line that tells us why an export shipped no photos.
  if (cells.size === 0) {
    console.warn(`[summary photos] Summary sheet "${summarySheet}" not found / empty — 0 photos.`);
    return { data: workbook, photos: 0 };
  }

  const slots = findPhotoSlots(cells);
  const prodEvidence = collectProdDataEvidence(prodData, gsProxy);
  const assignments = assignSlotPhotos(slots, fields, assets, answers, prodEvidence).slice(
    0,
    MAX_PHOTOS
  );
  const viaProxy = assignments.filter((a) => a.urls[0].includes("/v1/qa-video")).length;
  console.warn(
    `[summary photos] ${slots.length} slots, ${assignments.length} assigned` +
      (gsProxy ? ` (${viaProxy} reviewer-pasted gs://, ${assignments.length - viaProxy} direct)` : " (no gs:// proxy — reviewer-pasted evidence excluded)")
  );
  if (assignments.length === 0) return { data: workbook, photos: 0 };

  // Slot box geometry from the sheet's real row heights. The template's photo
  // boxes are square (the reference workbook's hand-placed photos are all
  // ≈2.28M EMU each way, aspect 0.99), sized by the 13-row slot height — so
  // the frame is height × height, and the absolute EMU extent makes Excel
  // stretch the picture to the full box instead of its natural size.
  const EMU_PER_PX = 9525;
  const BOX_INSET_PX = 4;
  const geometry = await readSheetGeometry(buf, summarySheet);
  const slotBox = (slot: PhotoSlot): { aspect: number; emu: SheetImage["emu"] } | null => {
    if (!geometry) return null;
    let x = 0;
    for (let c = 0; c < slot.col0; c++) x += geometry.colPx(c);
    let y = 0;
    for (let r = 1; r < slot.captionRow - SLOT_HEIGHT; r++) y += geometry.rowPx(r);
    let h = 0;
    for (let r = slot.captionRow - SLOT_HEIGHT; r < slot.captionRow; r++) h += geometry.rowPx(r);
    const side = h - BOX_INSET_PX;
    if (side <= 0) return null;
    return {
      aspect: 1,
      emu: {
        x: (x + BOX_INSET_PX / 2) * EMU_PER_PX,
        y: (y + BOX_INSET_PX / 2) * EMU_PER_PX,
        cx: side * EMU_PER_PX,
        cy: side * EMU_PER_PX,
      },
    };
  };

  // Try a slot's candidates in priority order, stopping at the first that
  // downloads — so a dead preferred source (e.g. reviewer gs:// with the proxy
  // down) falls back to the direct/asset photo rather than blanking the slot.
  let usedFallback = 0;
  const fetchForSlot = async (urls: string[]): Promise<Awaited<ReturnType<typeof fetchImage>>> => {
    for (let k = 0; k < urls.length; k++) {
      const img = await fetchImage(urls[k]);
      if (img) {
        if (k > 0) usedFallback++;
        return img;
      }
    }
    return null;
  };

  const images: SheetImage[] = [];
  for (let i = 0; i < assignments.length; i += FETCH_CONCURRENCY) {
    onProgress?.(`Adding photos (${Math.min(i + FETCH_CONCURRENCY, assignments.length)}/${assignments.length})…`);
    const batch = assignments.slice(i, i + FETCH_CONCURRENCY);
    const boxes = batch.map(({ slot }) => slotBox(slot));
    const fetched = await Promise.all(batch.map(({ urls }) => fetchForSlot(urls)));
    batch.forEach(({ slot }, j) => {
      const img = fetched[j];
      if (!img) return;
      // Center-crop to the (square) slot frame via srcRect, computed from the
      // real pixel size — so a wide panorama is trimmed, never squished, whether
      // or not the canvas pre-crop ran. Aspect defaults to 1 (the template's
      // boxes are square) when row/col geometry is unavailable.
      const size = imageSize(img.data);
      const srcRect = size ? coverSrcRect(size.w, size.h, boxes[j]?.aspect ?? 1) : undefined;
      images.push({
        ...img,
        fromCol: slot.col0,
        fromRow: slot.captionRow - 1 - SLOT_HEIGHT, // zero-based, 13 rows above the caption
        toCol: slot.col0 + SLOT_WIDTH + 1, // square frame reaches into the 4th column
        toRow: slot.captionRow - 1,
        emu: boxes[j]?.emu,
        srcRect,
        name: slot.description || undefined,
      });
    });
  }
  console.warn(
    `[summary photos] ${images.length}/${assignments.length} downloaded` +
      (usedFallback > 0 ? `, ${usedFallback} via fallback (primary source failed)` : "")
  );
  if (images.length === 0) return { data: workbook, photos: 0 };

  const data = await insertSheetImages(buf, summarySheet, images);
  return { data, photos: images.length };
}
